import { createAsyncThunk, createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import axiosInstance from '../../../../../app/config/axiosInstance';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';

interface OrderFilterState {
  data: {
    rescheduled: any[];
    currentDate: { date: string; count: number }[];
  };
  isLoading: boolean;
  error: string | null;
}

interface OrderState {
  data: any;
  isLoading: boolean;
  error: string | null;
  count?: number | null;
  type?: string;
}

export interface Load {
  id?: number;
  width: string;
  depth: string;
  height: string;
  unit: string;
  orderQuantity: string;
  weight: string;
  loadName?: string;
  orderIDs?: string;
  loadConfigID?: string;
  isActive?: number;
}

type NewLoad = Omit<Load, 'id'>;

type AddLoadPayload = {
  data: NewLoad;
  pageNo: number;
  pageSize: number;
  orderIDs?: string[];
  loadConfigID?: number;
};

type ThunkArgs = {
  id: number;
  updatePayload: {
    quantityLimit: number;
    radiusLimit: number;
    width: number;
    depth: number;
    height: number;
    weight: number;
    unit: string;
  };
  pageNo: number;
  pageSize: number;
};

const initialOrderFilterState: OrderFilterState = {
  data: {
    rescheduled: [],
    currentDate: []
  },
  isLoading: false,
  error: null
};

interface loadFilterState {
  data: any[];
  isLoading: boolean;
  error: string | null;
}

const initialLoadFilterState: loadFilterState = {
  data: [],
  isLoading: false,
  error: null
};

const initialOrderState: OrderState = {
  data: null,
  isLoading: false,
  error: null,
  count: null,
  type: 'loads'
};

// Interface for box data
export interface Box {
  boxId: number;
  barcodeId: string;
  boxDepth: number;
  boxWidth: number;
  boxHeight: number;
  boxWeight: number;
}

// Interface for order data
export interface Order {
  orderId: number;
  assignedOrderId: string;
  customerOrderId: string | null;
  imageURL: string | null;
  deliveredTo: string | null;
  totalBoxesCount: number;
  orderStatus: string;
  orderType: string;
  deliveryBoxes: Box[];
}

// Interface for load data (aligned with the API service)
// export interface Load {
//   lng: null;
//   lat: null;
//   id?: number;
//   loadName: string;
//   ordersCount: number;
//   loadStatus: number;
//   vehicleNumber: string | null;
//   depth: string;
//   width: string;
//   height: string;
//   boxWeight: string;
//   unit: string;
//   orderQuantity: string;
//   ordersResponses: Order[]; // Added to handle nested orders
// }
// Interface for fetch loads query params
type GetLoadsParams = {
  pageNo: number;
  pageSize: number;
  search?: string;
};

// Interface for the state
type initialStateProps = {
  isLoading: {
    addLoad: boolean;
    updateLoad: boolean;
    getLoad: boolean;
    deleteLoad: boolean;
    deactivateLoad: boolean;
  };
  data: Load[];
  error: string | null;
  count: number | null;
};

// Initial state
const initialState: initialStateProps = {
  isLoading: {
    addLoad: false,
    updateLoad: false,
    getLoad: false,
    deleteLoad: false,
    deactivateLoad: false
  },
  data: [],
  error: null,
  count: null
};

// Fetch loads thunk
export const getLoadsAction = createAsyncThunk<any, GetLoadsParams>(
  'load/all',
  async (data, thunkApi) => {
    const { pageNo, pageSize, search } = data;
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.GET_LOAD}`,
        params: {
          pageNo,
          pageSize,
          search: search || undefined
        }
      });
      return response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response?.data?.message || 'Failed to fetch loads',
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error.response?.data || error);
    }
  }
);

// Delete load thunk
export const deleteLoad = createAsyncThunk<
  number,
  { id: number; pageNo: number; pageSize: number }
>('delete/load', async (data, thunkApi) => {
  const { id, pageNo, pageSize } = data;
  try {
    const response = await axiosInstance({
      method: 'DELETE',
      url: `${URLs.DELETE_LOAD}?loadID=${id}`
    });
    thunkApi.dispatch(
      updateToast({
        show: true,
        message: response?.data?.message || 'Load deleted successfully',
        severity: 'success'
      })
    );
    return id;
  } catch (error: any) {
    thunkApi.dispatch(
      updateToast({
        show: true,
        message: error.response?.data?.message || 'Failed to delete load',
        severity: 'error'
      })
    );
    return thunkApi.rejectWithValue(error.response?.data || error);
  }
});

// Deactivate load thunk
export const deActivateLoad = createAsyncThunk<
  number,
  { id: number; pageNo: number; pageSize: number }
>('deactivate/load', async (data, thunkApi) => {
  const { id, pageNo, pageSize } = data;
  try {
    const response = await axiosInstance({
      method: 'PATCH',
      url: `${URLs.DEACTIVE_LOAD}?Id=${id}`
    });
    thunkApi.dispatch(
      updateToast({
        show: true,
        message: response?.data?.message || 'Load successfully',
        severity: 'success'
      })
    );
    return id;
  } catch (error: any) {
    thunkApi.dispatch(
      updateToast({
        show: true,
        message: error.response?.data?.message || 'Error Occur in Process',
        severity: 'error'
      })
    );
    return thunkApi.rejectWithValue(error.response?.data || error);
  }
});

// --- Thunks ---
export const getOrderFilterList = createAsyncThunk<any, any>(
  'load/order/filter',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance.get(
        `${URLs.GET_ORDERS_FILTER_LIST}?loadId=${data}`
      );
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

export const getOrderFilter = createAsyncThunk<any, any>(
  'load/order',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance.get(
        `${URLs.FILTERED_ORDER}?isScheduled=${data?.isScheduledOrder}&date=${
          data?.selectedOrdersByDate
        }${data?.loadID ? `&loadID=${data?.loadID}` : ''}`
      );
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

export const getOrderLoadConfig = createAsyncThunk<any, any>(
  'load/order/config',
  async (data, thunkApi) => {
    try {
      // const response = await axiosInstance.post(URLs.GET_ORDER_LOADCONFIG, data);
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.GET_ORDER_LOADCONFIG}`,
        data
      });
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

export const getOrderLoadConfigDimension = createAsyncThunk<any, any>(
  'load/order/config/Dimension',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance.post(
        URLs.GET_ORDER_LOADCONFIG_DIMENSIONS,
        data
      );
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

export const addLoad = createAsyncThunk<any, AddLoadPayload>(
  'load/add',
  async (params, thunkApi) => {
    const { data, pageNo, pageSize, orderIDs = [], loadConfigID } = params;
    try {
      const response = await axiosInstance.post(URLs.ADD_LOAD, params);
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message || 'Load added successfully',
          severity: 'success'
        })
      );
      return response?.data?.data || response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message || 'Failed to add load',
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error?.response?.data || error);
    }
  }
);

export const updateLoad = createAsyncThunk<any, AddLoadPayload>(
  'load/upade',
  async (params, thunkApi) => {
    const { data, pageNo, pageSize, orderIDs = [], loadConfigID } = params;
    try {
      const response = await axiosInstance.put(URLs.UPDATE_LOAD, params);
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message || 'Load added successfully',
          severity: 'success'
        })
      );
      return response?.data?.data || response?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.message || 'Failed to add load',
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error?.response?.data || error);
    }
  }
);

// --- Slices ---

export const getOrderFilterListSlice = createSlice({
  name: 'orderFilter',
  initialState: initialOrderFilterState,
  reducers: {
    clearLoadAction: state => {
      state.data = { rescheduled: [], currentDate: [] };
      state.isLoading = false;
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getOrderFilterList.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrderFilterList.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.data = action.payload || { rescheduled: [], currentDate: [] };
      })
      .addCase(getOrderFilterList.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          typeof action.payload === 'string' ? action.payload : 'Something went wrong';
      });
  }
});

export const getOrderFilterSlice = createSlice({
  name: 'orderFilteredData',
  initialState: initialOrderState,
  reducers: {
    storeOrderFilterData: (state, action) => {
      if (typeof action.payload === 'boolean') {
        state.data = state.data.map((item: any, idx: number) => ({
          ...item,
          id: idx + 1,
          lat: item.deliverableLat,
          lng: item.deliverableLng,
          name: 'trip',
          info: { Mylocation: item?.orderId || '' },
          selected: action.payload
        }));
      } else if (action.payload?.length > 0) {
        state.data = action.payload;
      } else {
        state.data = state.data.map((item: any, idx: number) =>
          item.orderId === action.payload?.orderId
            ? {
                ...item,
                id: idx + 1,
                lat: item.deliverableLat,
                lng: item.deliverableLng,
                name: 'trip',
                info: { Mylocation: item.orderId },
                selected: action.payload?.isSelected
              }
            : { ...item }
        );
      }
    },
    clearOrderFilterData: state => {
      state.data = [];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getOrderFilter.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrderFilter.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.data = (action.payload || []).map((item: any, idx: number) => {
          const match = state.data?.find(
            (prev: any) => prev.orderId === item.orderId && prev.selected
          );
          return {
            ...item,
            id: idx + 1,
            lat: item.deliverableLat,
            lng: item.deliverableLng,
            name: 'trip',
            info: { Mylocation: item.orderId },
            selected: !!match
          };
        });
      })
      .addCase(getOrderFilter.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          typeof action.payload === 'string' ? action.payload : 'Something went wrong';
      });
  }
});

export const getOrderLoadConfigSlice = createSlice({
  name: 'loadConfiguration',
  initialState: initialLoadFilterState,
  reducers: {
    storeLoadConfigData: (state, action) => {
      state.data = current(state).data?.map((item: any, idx: number) => ({
        ...item,
        selected: action?.payload?.id === item?.id ? true : false,
        preferred: idx === 0 ? true : false
      }));
    },
    clearLoadConfigData: state => {
      state.data = [];
    }
  },

  extraReducers: builder => {
    builder
      .addCase(getOrderLoadConfig.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrderLoadConfig.fulfilled, (state, action: any) => {
        state.isLoading = false;
        // state.data = action.payload;
        state.data = (action.payload || []).map((item: any, idx: number) => {
          const match = state.data?.find(
            (prev: any) => prev.id === item.id && prev.selected
          );
          return {
            ...item,
            preferred: idx === 0 ? true : false,
            selected: !!match
          };
        });
      })
      .addCase(getOrderLoadConfig.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          typeof action.payload === 'string' ? action.payload : 'Something went wrong';
      });
  }
});

export const getOrderLoadConfigDimensionSlice = createSlice({
  name: 'loadConfigDimension',
  initialState: initialOrderFilterState,
  reducers: {
    clearDimension: state => {
      state.data = { rescheduled: [], currentDate: [] };
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getOrderLoadConfigDimension.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        getOrderLoadConfigDimension.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = action.payload || { rescheduled: [], currentDate: [] };
        }
      )
      .addCase(getOrderLoadConfigDimension.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          typeof action.payload === 'string' ? action.payload : 'Something went wrong';
      });
  }
});

// Create the slice
const loadSlice = createSlice({
  name: 'Overallload',
  initialState,
  reducers: {},
  extraReducers: builder => {
    // getLoadsAction
    builder
      .addCase(getLoadsAction.pending, state => {
        state.isLoading.getLoad = true;
        state.error = null;
      })
      .addCase(getLoadsAction.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading.getLoad = false;
        const { content, count } = action.payload?.data || {};
        state.data =
          content?.map((item: any) => ({
            ...item,
            id: item.loadID ?? null,
            loadName: item.loadName ?? '',
            ordersCount: item.ordersCount ?? 0,
            loadStatus: item.loadStatus ?? 0,
            vehicleNumber: item.vehicleNumber ?? null,
            depth: String(item.depth ?? '0'),
            width: String(item.width ?? '0'),
            height: String(item.height ?? '0'),
            boxWeight: String(item.boxWeight ?? '0'),
            unit: item.unit ?? 'cm',
            orderQuantity: String(item.orderQuantity ?? '0'),
            ordersResponses:
              item.ordersResponses?.map((order: any) => ({
                ...order,
                orderId: order.orderId ?? 0,
                assignedOrderId: order.assignedOrderId ?? '',
                customerOrderId: order.customerOrderId ?? null,
                imageURL: order.imageURL ?? null,
                deliveredTo: order.deliveredTo ?? null,
                totalBoxesCount: order.totalBoxesCount ?? 0,
                orderStatus: order.orderStatus ?? '',
                orderType: order.orderType ?? '',
                deliveryBoxes:
                  order.deliveryBoxes?.map((box: any) => ({
                    ...box,
                    boxId: box.boxId ?? 0,
                    barcodeId: box.barcodeId ?? '',
                    boxDepth: box.boxDepth ?? 0,
                    boxWidth: box.boxWidth ?? 0,
                    boxHeight: box.boxHeight ?? 0,
                    boxWeight: box.boxWeight ?? 0
                  })) ?? []
              })) ?? []
          })) || [];
        state.count = count ?? content?.length ?? 0;
        state.error = null;
      })
      .addCase(getLoadsAction.rejected, (state, action: any) => {
        state.isLoading.getLoad = false;
        state.error = action.payload?.message || 'Failed to fetch loads';
      })
      //add
      .addCase(addLoad.pending, state => {
        state.isLoading.addLoad = true;
        state.error = null;
      })
      .addCase(addLoad.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading.addLoad = false;
        state.error = null;
      })
      .addCase(addLoad.rejected, (state, action: any) => {
        state.isLoading.addLoad = false;
        state.error = action.payload?.message || 'Failed to add load';
      })
      //update
      .addCase(updateLoad.pending, state => {
        state.isLoading.updateLoad = true;
        state.error = null;
      })
      .addCase(updateLoad.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading.updateLoad = false;
        state.error = null;
      })
      .addCase(updateLoad.rejected, (state, action: any) => {
        state.isLoading.updateLoad = false;
        state.error = action.payload?.message || 'Failed to add load';
      })

      // deleteLoad
      .addCase(deleteLoad.pending, state => {
        state.isLoading.deleteLoad = true;
        state.error = null;
      })
      .addCase(deleteLoad.fulfilled, (state, action: PayloadAction<number>) => {
        state.isLoading.deleteLoad = false;
        state.error = null;
      })
      .addCase(deleteLoad.rejected, (state, action: any) => {
        state.isLoading.deleteLoad = false;
        state.error = action.payload?.message || 'Failed to delete load';
      })
      //deactivated
      .addCase(deActivateLoad.pending, state => {
        state.isLoading.deactivateLoad = true;
        state.error = null;
      })
      .addCase(deActivateLoad.fulfilled, (state, action: PayloadAction<number>) => {
        state.isLoading.deactivateLoad = false;
      })
      .addCase(deActivateLoad.rejected, (state, action: any) => {
        state.isLoading.deactivateLoad = false;
        state.error = action.payload?.message || 'Failed to delete load';
      });
  }
});

export const { clearLoadAction } = getOrderFilterListSlice.actions;
export const { storeOrderFilterData } = getOrderFilterSlice.actions;
export const { clearOrderFilterData } = getOrderFilterSlice.actions;
export const { storeLoadConfigData } = getOrderLoadConfigSlice.actions;
export const { clearLoadConfigData } = getOrderLoadConfigSlice.actions;
export const { clearDimension } = getOrderLoadConfigDimensionSlice.actions;

export const getOrderFilterListReducer = getOrderFilterListSlice.reducer;
export const getOrderFilterReducer = getOrderFilterSlice.reducer;
export const getOrderLoadConfigReducer = getOrderLoadConfigSlice.reducer;
export const getOrderLoadConfigDimensionReducer =
  getOrderLoadConfigDimensionSlice.reducer;
export const loadReducer = loadSlice.reducer;
